import { Label } from '@/components/ui/label';
import { formatDateTime } from '@/lib/utils';
import type { TransmitterV2Document } from '../../types';

function formatDateOrDash(dateString: string | null | undefined): string {
  if (!dateString) return '--';
  return formatDateTime(dateString);
}

function getRssiLabel(rssi: number): string {
  if (rssi >= 6) return 'Excellent';
  if (rssi >= 4) return 'Good';
  if (rssi >= 2) return 'Fair';
  if (rssi >= 1) return 'Poor';
  return 'None';
}

interface StatusSectionProps {
  transmitter: TransmitterV2Document;
}

export function StatusSection({ transmitter }: StatusSectionProps) {
  const rssi = transmitter.rssi;
  const battery = transmitter.battery_percentage;
  const voltage = transmitter.battery_voltage;

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
        Status
      </h3>

      {/* Last Packet */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>Last Packet At</Label>
        <span className="text-sm">{formatDateOrDash(transmitter.lastpacket_at)}</span>
      </div>

      {/* Battery */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>Battery</Label>
        <span className="text-sm">
          {battery != null ? (
            <span
              className={
                Number(battery) >= 50
                  ? 'text-green-600'
                  : Number(battery) >= 20
                    ? 'text-yellow-600'
                    : 'text-red-600'
              }
            >
              {Math.round(Number(battery))}%
              {voltage != null && ` (${Number(voltage).toFixed(2)}V)`}
            </span>
          ) : voltage != null ? (
            <span
              className={
                Number(voltage) > 3.4
                  ? 'text-green-600'
                  : Number(voltage) > 2.9
                    ? 'text-yellow-600'
                    : 'text-red-600'
              }
            >
              {Number(voltage).toFixed(2)}V
            </span>
          ) : (
            <span className="text-muted-foreground">No data yet</span>
          )}
        </span>
      </div>

      {/* External Power */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>External Power</Label>
        <span className="text-sm">
          {transmitter.power_external ? 'External Power' : 'Using battery'}
        </span>
      </div>

      {/* RSSI */}
      <div className="grid grid-cols-[140px_1fr] items-center gap-2">
        <Label>RSSI</Label>
        <span className="text-sm">
          {rssi != null ? (
            <span
              className={
                rssi >= 4
                  ? 'text-green-600'
                  : rssi >= 2
                    ? 'text-yellow-600'
                    : 'text-red-600'
              }
            >
              {rssi} ({getRssiLabel(rssi)})
            </span>
          ) : (
            <span className="text-muted-foreground">--</span>
          )}
        </span>
      </div>
    </div>
  );
}
